import React, { useState, useEffect } from 'react';
import { auth, db } from './firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import Auth from './components/Auth';
import Contacts from './screens/Contacts';
import AddProduct from './screens/AddProduct';
import ProductList from './components/ProductList';
import LeftPanel from './components/LeftPanel';
import FullSpec from './screens/FullSpec';
import CompanyDashboard from './screens/CompanyDashboard';
import IndividualProfit from './screens/IndividualProfit';
import SalesHistory from './screens/SalesHistory';
import ComparisonList from './screens/ComparisonList';
import EditItemScreen from './screens/EditItemScreen';
import Footer from './components/Footer';
import AddItemForm from './components/AddItemForm';
import ReportingCenter from './screens/ReportingCenter';
import CompetitorsPage from './screens/CompetitorsPage';
import AddAnalysisPage from './screens/AddAnalysisPage';
import tmgLogo from './assets/tmg_logo_final.png'; // Make sure your logo is named this in src/assets/
import './App.css';

const initialCategoryPrefixMap = {
    "Traditional Jewels": "TTJ", "Kundans": "KND", "Kemps": "KMP", "Chokers": "CHK", "Chains": "CHN",
    "Rings": "RNG", "Bangles": "BGL", "Earrings": "ERG", "American Diamond": "AD", "Shoes": "SHS",
    "Bags": "BAG", "Interiors": "INT", "Statues": "STU", "Ornaments": "ORN", "Elixirs": "ELX",
    "Gifts": "GFT", "Sea Treasure": "STR"
};

const MainPortal = ({ userProfile, theme, toggleTheme }) => {
  const [view, setView] = useState('companyDashboard');
  const [editingProductId, setEditingProductId] = useState(null);
  const [viewingProductId, setViewingProductId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [addProductKey, setAddProductKey] = useState(Date.now());
  const [preAppliedFilter, setPreAppliedFilter] = useState(null);
  const [addingToParent, setAddingToParent] = useState(null);
  const [newChildId, setNewChildId] = useState('');
  const [categoryPrefixMap, setCategoryPrefixMap] = useState(initialCategoryPrefixMap);
  const [categories, setCategories] = useState(Object.keys(initialCategoryPrefixMap).sort());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleEditProduct = (productId) => {
    setEditingProductId(productId);
    setAddProductKey(Date.now());
    setView('addProduct');
  };

  const handleViewProduct = (productId) => {
    setViewingProductId(productId);
    setView('fullSpec');
  };
  
  const handleEditItem = (itemId) => {
    setEditingItemId(itemId);
    setView('editItem');
  };

  const handleAddNewChild = (parentProduct, generatedChildId) => {
      setAddingToParent(parentProduct);
      setNewChildId(generatedChildId);
      setView('addNewChild');
  };

  const handleNavigateToAddAnalysis = () => {
    setView('addAnalysis');
  };

  const handleNavigateToEditAnalysis = (analysisId) => {
    // This logic is part of the CompetitorsPage itself, App.js just needs to render it.
    // We can pass the ID if a dedicated edit page is ever needed.
    // For now, CompetitorsPage handles its own edit state.
  };

  const handleFinishAction = () => {
    window.scrollTo(0, 0);
    setEditingProductId(null);
    setViewingProductId(null);
    setEditingItemId(null);
    setPreAppliedFilter(null);
    setAddingToParent(null);
    setView('dashboard');
  };

  const handleAddNewProduct = () => {
    setEditingProductId(null);
    setAddProductKey(Date.now());
    setView('addProduct');
  };

  const handleNavigateWithFilter = (targetView, filter) => {
    setPreAppliedFilter(filter);
    setView(targetView);
  };

  return (
    <div className="portal-container">
      <header className="app-header">
        <div className="top-bar">
          <div className="header-title">
            <button onClick={() => window.location.reload()} className="logo-button">
                <img src={tmgLogo} alt="TMG iDASH Logo" />
            </button>
          </div>
          <div className="header-logo-container">
            <div className="header-logo-text">
              <span className="main-title">THEERTANA MAHLIGAI</span>
              <span className="subtitle">ROYAL MAJESTY COLLECTIONS</span>
            </div>
          </div>
          <div className="header-user-controls">
            <p>Welcome, {userProfile.displayName}</p>
            <button onClick={toggleTheme} className="theme-toggle" title="Toggle Theme">{theme === 'dark' ? '☀️' : '🌙'}</button>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
        <nav className="main-nav">
          {(userProfile.role === 'Founder' || userProfile.role === 'super-admin' || userProfile.role === 'admin') && (
            <>
              <button onClick={() => setView('companyDashboard')}>Company Dashboard</button>
              <button onClick={() => setView('individualProfit')}>Individual Profit</button>
              <button onClick={() => setView('salesHistory')}>Sales History</button>
              <button onClick={() => setView('reporting')}>Reporting</button>
              <button onClick={() => setView('comparisons')}>Comparisons</button>
              <button onClick={() => setView('competitors')}>Competitors</button>
            </>
          )}
          <button onClick={() => { setView('dashboard'); setCategoryFilter(null); }}>Product Overview</button>
          <button onClick={handleAddNewProduct}>+ Add Product</button>
          <button onClick={() => setView('contacts')}>Manage Contacts</button>
        </nav>
      </header>

      <div className={`portal-body ${view === 'dashboard' ? 'with-sidebar' : ''}`}>
        {view === 'dashboard' && <LeftPanel categories={categories} activeCategory={categoryFilter} onSelectCategory={setCategoryFilter} />}
        <main className="main-content">
          {view === 'dashboard' && <ProductList user={userProfile} onEdit={handleEditProduct} onView={handleViewProduct} onEditItem={handleEditItem} onAddNewChild={handleAddNewChild} categoryFilter={categoryFilter} />}
          {view === 'companyDashboard' && <CompanyDashboard user={userProfile} onNavigateWithFilter={handleNavigateWithFilter} />}
          {view === 'individualProfit' && <IndividualProfit user={userProfile} setView={setView} />}
          {view === 'salesHistory' && <SalesHistory user={userProfile} setView={setView} preAppliedFilter={preAppliedFilter} />}
          {view === 'reporting' && <ReportingCenter user={userProfile} setView={setView} />}
          {view === 'competitors' && <CompetitorsPage user={userProfile} setView={setView} onNavigateToAddAnalysis={handleNavigateToAddAnalysis} onNavigateToEditAnalysis={handleNavigateToEditAnalysis} />}
          {view === 'addAnalysis' && <AddAnalysisPage user={userProfile} setView={setView} />}
          {view === 'comparisons' && <ComparisonList user={userProfile} />}
          {view === 'contacts' && <Contacts user={userProfile} setView={setView} />}
          {view === 'addProduct' && <AddProduct key={addProductKey} user={userProfile} setView={handleFinishAction} onAddNew={handleAddNewProduct} productIdToEdit={editingProductId} categoryPrefixMap={categoryPrefixMap} setCategoryPrefixMap={setCategoryPrefixMap} categories={categories} setCategories={setCategories} />}
          {view === 'fullSpec' && <FullSpec user={userProfile} productId={viewingProductId} onClose={handleFinishAction} />}
          {view === 'editItem' && <EditItemScreen user={userProfile} setView={handleFinishAction} itemIdToEdit={editingItemId} />}
          {view === 'addNewChild' && <AddItemForm user={userProfile} parentProduct={addingToParent} newChildId={newChildId} setView={setView} />}
        </main>
      </div>
      
      {!['fullSpec', 'editItem', 'addProduct', 'addNewChild', 'reporting', 'competitors', 'addAnalysis'].includes(view) && <Footer />}
    </div>
  );
};

function App() {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const profile = userDocSnap.data();
          if (user.email === 'rughaofficial@gmail.com') {
             profile.role = 'super-admin';
          } else if (user.email === 'thanalacumi060992@gmail.com') {
             profile.role = 'admin';
          }
          setUserProfile({ uid: user.uid, ...profile });
        } else {
          signOut(auth);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="App">
      {userProfile ? <MainPortal userProfile={userProfile} theme={theme} toggleTheme={toggleTheme} /> : <Auth theme={theme} toggleTheme={toggleTheme} />}
    </div>
  );
}

export default App;